import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import authEn from '../locales/en/auth.json';
import sidebarEn from '../locales/en/sidebar.json';
import accountEn from '../locales/en/account.json';
import orgSettingsEn from '../locales/en/orgSettings.json';
import subscriptionEn from '../locales/en/subscription.json';
import usersEn from '../locales/en/users.json';
import commonEn from '../locales/en/common.json';
import projectEn from '../locales/en/project.json';
import componentsEn from '../locales/en/components.json';
import onboardingEn from '../locales/en/onboarding.json';
import featureGateEn from '../locales/en/featureGate.json';

const supportedLngs = ['en', 'es', 'de', 'fr', 'hu'] as const;

export const defaultNS = 'auth' as const;
export const resources = {
  en: {
    auth: authEn,
    sidebar: sidebarEn,
    account: accountEn,
    orgSettings: orgSettingsEn,
    subscription: subscriptionEn,
    users: usersEn,
    common: commonEn,
    project: projectEn,
    components: componentsEn,
    onboarding: onboardingEn,
    featureGate: featureGateEn,
  },
} as const;

export type SupportedLanguage = (typeof supportedLngs)[number];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use({
    type: 'backend' as const,
    read(language: string, namespace: string, callback: (err: Error | null, data: Record<string, string> | null) => void) {
      const lang = supportedLngs.includes(language as SupportedLanguage) ? language : 'en';
      import(`../locales/${lang}/${namespace}.json`)
        .then((module) => callback(null, module.default))
        .catch((error) => callback(error, null));
    },
  })
  .init({
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    supportedLngs,
    fallbackLng: 'en',
    nonExplicitSupportedLngs: true,
    debug: import.meta.env.DEV,
    defaultNS: 'auth',
    ns: ['auth', 'sidebar', 'account', 'orgSettings', 'subscription', 'users', 'common', 'project', 'components', 'onboarding', 'featureGate'],
    react: {
      useSuspense: true,
      // Re-render on resource-bundle changes so the dev locale hot-swap
      // below (and lazy namespace loads) show up without a language change.
      bindI18nStore: 'added removed',
    },
  });

// Dev-only: localeHmrPlugin (vite.config.ts) intercepts locale JSON edits and
// ships the fresh translations over this event instead of letting HMR
// propagate to main.tsx (which would force a full page reload).
if (import.meta.hot) {
  import.meta.hot.on('locale-update', ({ lang, ns, resources }: { lang: string; ns: string; resources: Record<string, unknown> }) => {
    i18n.removeResourceBundle(lang, ns);
    i18n.addResourceBundle(lang, ns, resources, true, true);
  });
}

export const languageNames: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  hu: 'Magyar',
};

export { supportedLngs };
export default i18n;
